/**
 * SuperAdminTemplates — central management for every template type used
 * across AutoShopFlow. Super Admin creates them once; the rest of the app
 * loads them on demand (invoice editor, SMS modal, email composer, JC
 * Quality Checklist, etc.).
 *
 * 7 template types are grouped under tabs:
 *   - Invoice Item     → quick-add line item (description / parts / labour)
 *   - Notes            → generic free-text snippet
 *   - Service Notes    → service-recommendation snippet
 *   - SMS              → text-message template (booking, ready-for-pickup, …)
 *   - Email            → email subject + body
 *   - Promotional      → marketing-blast template (channel = sms | email | both)
 *   - Service Checklist→ named QC list used in Job Card Step 4
 *
 * Each tab gets the same shell: list rows + Create/Edit dialog. Form
 * fields adapt to the template type.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Copy, Edit3, FileText, MessageSquare, Mail, ClipboardCheck, Receipt, Megaphone, NotebookText, Truck, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { key: 'service_checklist', label: 'Service Checklists', Icon: ClipboardCheck, description: 'QC lists used in Job Card Step 4. Pick one when starting a job to match the work scope.' },
  { key: 'invoice_item',      label: 'Invoice Items',      Icon: Receipt,        description: 'Re-usable line items dropped into invoices and quotes — supplier, ORP/RRP, margin, qty, labour rate & hours all pre-populated on click.' },
  { key: 'supplier',          label: 'Suppliers',          Icon: Truck,          description: 'Curated supplier list — these appear in the SupplierCombobox on every invoice line & service-notes row.' },
  { key: 'notes',             label: 'Notes',              Icon: NotebookText,   description: 'Generic notes a user can drop anywhere there is a free-text field.' },
  { key: 'service_notes',     label: 'Service Notes',      Icon: FileText,       description: 'Recommendation snippets for the Service Notes section on invoices.' },
  { key: 'sms',               label: 'SMS Templates',      Icon: MessageSquare,  description: 'Text messages sent from the Customer Communications screen.' },
  { key: 'email',             label: 'Email Templates',    Icon: Mail,           description: 'Email subject + body. Used when sending quotes, invoices, follow-ups.' },
  { key: 'promotional',       label: 'Promotional',        Icon: Megaphone,      description: 'Marketing blasts (sms / email / both) sent via Marketing Engagement Hub.' },
];

const emptyDraftByType = (t) => {
  const base = { name: '', is_global: true };
  switch (t) {
    case 'service_checklist': return { ...base, content: { default: false, items: [{ key: '', label: '' }] } };
    case 'invoice_item':      return { ...base, content: {
      description: '', category: 'part_and_labour',
      parts_supplier: '', parts_cost_price: 0, parts_price: 0, parts_margin_pct: 40,
      quantity: 1, labour_hours: 0, labour_rate: 125.5,
    } };
    case 'supplier':          return { ...base, content: { contact: '', phone: '', email: '', address: '', account_number: '', notes: '' } };
    case 'notes':             return { ...base, content: { body: '' } };
    case 'service_notes':     return { ...base, content: { body: '' } };
    case 'sms':               return { ...base, content: { body: '' } };
    case 'email':             return { ...base, content: { subject: '', body: '' } };
    case 'promotional':       return { ...base, content: { channel: 'both', subject: '', body: '' } };
    default:                  return { ...base, content: {} };
  }
};

export default function SuperAdminTemplates() {
  const [activeType, setActiveType] = useState('service_checklist');
  const [templatesByType, setTemplatesByType] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {mode: 'create'|'edit', template}

  const loadTemplates = async (type) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/template-library?template_type=${type}&include_inactive=true`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setTemplatesByType(prev => ({ ...prev, [type]: data.templates || [] }));
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { loadTemplates(activeType);   }, [activeType]);

  const openCreate = () => setEditing({ mode: 'create', template: emptyDraftByType(activeType) });
  const openEdit   = (t) => setEditing({ mode: 'edit', template: JSON.parse(JSON.stringify(t)) });

  const saveTemplate = async () => {
    const { mode, template } = editing;
    const payload = mode === 'create'
      ? { template_type: activeType, name: template.name, content: template.content, is_global: template.is_global }
      : { name: template.name, content: template.content, is_active: template.is_active ?? true };
    try {
      const url = mode === 'create' ? '/api/template-library' : `/api/template-library/${template.template_id}`;
      const res = await fetch(`${BACKEND_URL}${url}`, {
        method: mode === 'create' ? 'POST' : 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Save failed');
      }
      toast.success(`Template ${mode === 'create' ? 'created' : 'updated'}`);
      setEditing(null);
      await loadTemplates(activeType);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const duplicateTemplate = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/template-library/${id}/duplicate`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error('Duplicate failed');
      toast.success('Duplicated');
      await loadTemplates(activeType);
    } catch (e) { toast.error(e.message); }
  };

  const deleteTemplate = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"? This is soft-delete; super admin can restore via DB if needed.`)) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/template-library/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await loadTemplates(activeType);
    } catch (e) { toast.error(e.message); }
  };

  const currentList = templatesByType[activeType] || [];

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="sa-templates-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Templates</h1>
        <p className="text-sm text-slate-600 mt-1">
          One library of reusable snippets — checklists, invoice items, SMS, email, notes & promotional content.
          Global templates (the default) are visible to every workshop; site-scoped templates only show in the
          workshop that created them.
        </p>
      </div>

      <Tabs value={activeType} onValueChange={setActiveType} className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-1 h-auto p-1">
          {TYPES.map(t => (
            <TabsTrigger key={t.key} value={t.key} className="text-xs md:text-sm py-2 px-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid={`tpl-tab-${t.key}`}>
              <t.Icon className="h-3.5 w-3.5 mr-1.5 inline" />
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {TYPES.map(t => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <div className="flex items-start justify-between mb-3 gap-3">
              <p className="text-sm text-slate-600 max-w-2xl">{t.description}</p>
              <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 flex-shrink-0" data-testid={`tpl-create-${t.key}`}>
                <Plus className="h-4 w-4 mr-1" /> New {t.label.replace(/s$/, '')}
              </Button>
            </div>

            {loading && <p className="text-sm text-slate-400 italic">Loading…</p>}
            {!loading && currentList.length === 0 && (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm">
                No templates yet. Click <strong>New {t.label.replace(/s$/, '')}</strong> to add one.
              </div>
            )}

            {!loading && currentList.length > 0 && (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {currentList.map(tpl => (
                  <div key={tpl.template_id} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`tpl-row-${tpl.template_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{tpl.name}</span>
                        {tpl.site_id === null && <Badge variant="outline" className="text-[10px]">Global</Badge>}
                        {tpl.content?.default && <Badge className="text-[10px] bg-blue-100 text-blue-800 hover:bg-blue-100">Default</Badge>}
                        {!tpl.is_active && <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-700">Inactive</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{summary(t.key, tpl.content)}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(tpl)} title="Edit" data-testid={`tpl-edit-${tpl.template_id}`}>
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => duplicateTemplate(tpl.template_id)} title="Duplicate">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteTemplate(tpl.template_id, tpl.name)} title="Delete" data-testid={`tpl-delete-${tpl.template_id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {editing && (
        <TemplateEditor
          mode={editing.mode}
          type={activeType}
          template={editing.template}
          onChange={(template) => setEditing({ ...editing, template })}
          onClose={() => setEditing(null)}
          onSave={saveTemplate}
        />
      )}
    </div>
  );
}

function summary(type, content) {
  if (!content) return '';
  switch (type) {
    case 'service_checklist': return `${content.items?.length || 0} items${content.default ? ' · default' : ''}`;
    case 'invoice_item': {
      const parts = Number(content.parts_price) || 0;
      const margin = Number(content.parts_margin_pct) || 0;
      const hrs = Number(content.labour_hours) || 0;
      const rate = Number(content.labour_rate) || 0;
      const qty = Number(content.quantity) || 1;
      const total = ((parts * (1 + margin / 100)) + hrs * rate) * qty;
      return `${content.description} · ${content.parts_supplier ? content.parts_supplier + ' · ' : ''}$${parts.toFixed(2)} parts · ${hrs}h labour · ≈$${total.toFixed(2)}`;
    }
    case 'supplier':          return [content.contact, content.phone, content.email].filter(Boolean).join(' · ');
    case 'notes':
    case 'service_notes':
    case 'sms':               return (content.body || '').slice(0, 100);
    case 'email':             return `${content.subject || ''} — ${(content.body || '').slice(0, 80)}`;
    case 'promotional':       return `[${content.channel || 'both'}] ${(content.subject || content.body || '').slice(0, 80)}`;
    default: return '';
  }
}

/* ─────────────────────────  TemplateEditor  ───────────────────────── */

function TemplateEditor({ mode, type, template, onChange, onClose, onSave }) {
  const set = (path, val) => {
    // Tiny dot-path setter — keeps the JSX terse below.
    const parts = path.split('.');
    const next = JSON.parse(JSON.stringify(template));
    let ref = next;
    for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]] ??= {};
    ref[parts[parts.length - 1]] = val;
    onChange(next);
  };

  const setItem = (idx, key, val) => {
    const items = [...(template.content.items || [])];
    items[idx] = { ...items[idx], [key]: val };
    onChange({ ...template, content: { ...template.content, items } });
  };
  const addItem = () => {
    onChange({
      ...template,
      content: { ...template.content, items: [...(template.content.items || []), { key: '', label: '' }] },
    });
  };
  const removeItem = (idx) => {
    const items = [...(template.content.items || [])];
    items.splice(idx, 1);
    onChange({ ...template, content: { ...template.content, items } });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="!max-w-2xl">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New' : 'Edit'} Template</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="tpl-name">Name</Label>
            <Input
              id="tpl-name"
              value={template.name || ''}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Standard Service Checklist"
              data-testid="tpl-editor-name"
            />
          </div>

          {/* Type-specific fields */}
          {type === 'service_checklist' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Checklist Items</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="tpl-default" className="text-xs text-slate-600">Default for new JCs</Label>
                  <Switch id="tpl-default" checked={!!template.content?.default} onCheckedChange={(v) => set('content.default', v)} data-testid="tpl-editor-default" />
                </div>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {(template.content.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={it.label}
                      onChange={(e) => setItem(i, 'label', e.target.value)}
                      placeholder="Check item label (eg. Brakes inspected)"
                      data-testid={`tpl-editor-item-label-${i}`}
                    />
                    <Input
                      value={it.key}
                      onChange={(e) => setItem(i, 'key', e.target.value)}
                      placeholder="key (auto)"
                      className="w-44 font-mono text-xs"
                    />
                    <Button variant="ghost" size="sm" onClick={() => removeItem(i)} disabled={(template.content.items || []).length <= 1}>
                      <X className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addItem} data-testid="tpl-editor-add-item">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add item
              </Button>
              <p className="text-[11px] text-slate-500">
                Leave <code>key</code> empty — the server auto-derives it from the label. Toggle Default to make
                this the auto-selected checklist when a new job card opens.
              </p>
            </div>
          )}

          {type === 'invoice_item' && (
            <InvoiceItemEditor template={template} set={set} />
          )}

          {type === 'supplier' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Contact Person</Label>
                <Input value={template.content.contact || ''} onChange={(e) => set('content.contact', e.target.value)} placeholder="e.g. John Smith" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={template.content.phone || ''} onChange={(e) => set('content.phone', e.target.value)} placeholder="03 9XXX XXXX" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={template.content.email || ''} onChange={(e) => set('content.email', e.target.value)} placeholder="orders@supplier.com" />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input value={template.content.address || ''} onChange={(e) => set('content.address', e.target.value)} placeholder="Street, suburb, state, postcode" />
              </div>
              <div>
                <Label>Account Number</Label>
                <Input value={template.content.account_number || ''} onChange={(e) => set('content.account_number', e.target.value)} />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={template.content.notes || ''} onChange={(e) => set('content.notes', e.target.value)} placeholder="e.g. Same-day delivery to workshop" />
              </div>
              <p className="col-span-2 text-[11px] text-slate-500">
                The supplier <strong>Name</strong> (set above) is what shows up in the SupplierCombobox dropdown across every invoice line and service-notes row.
              </p>
            </div>
          )}

          {(type === 'notes' || type === 'service_notes' || type === 'sms') && (
            <div>
              <Label>{type === 'sms' ? 'SMS Body (160 chars per part)' : 'Body'}</Label>
              <Textarea
                value={template.content.body || ''}
                onChange={(e) => set('content.body', e.target.value)}
                rows={6}
                placeholder={type === 'sms' ? 'Hi {customer_name}, your car is ready…' : 'Body text…'}
                data-testid="tpl-editor-body"
              />
              {type === 'sms' && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Tokens: {'{customer_name}'}, {'{vehicle_rego}'}, {'{vehicle_make}'}, {'{vehicle_model}'}, {'{booking_date}'}
                </p>
              )}
            </div>
          )}

          {type === 'email' && (
            <>
              <div>
                <Label>Subject</Label>
                <Input value={template.content.subject || ''} onChange={(e) => set('content.subject', e.target.value)} />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea value={template.content.body || ''} onChange={(e) => set('content.body', e.target.value)} rows={8} />
              </div>
            </>
          )}

          {type === 'promotional' && (
            <>
              <div>
                <Label>Channel</Label>
                <Select value={template.content.channel || 'both'} onValueChange={(v) => set('content.channel', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sms">SMS only</SelectItem>
                    <SelectItem value="email">Email only</SelectItem>
                    <SelectItem value="both">SMS + Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject (for Email channel)</Label>
                <Input value={template.content.subject || ''} onChange={(e) => set('content.subject', e.target.value)} />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea value={template.content.body || ''} onChange={(e) => set('content.body', e.target.value)} rows={5} />
              </div>
            </>
          )}

          {mode === 'create' && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <Switch id="tpl-global" checked={template.is_global !== false} onCheckedChange={(v) => set('is_global', v)} data-testid="tpl-editor-is-global" />
              <Label htmlFor="tpl-global" className="text-sm">Make global (visible to all workshops). Uncheck to scope to your own workshop.</Label>
            </div>
          )}
          {mode === 'edit' && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <Switch id="tpl-active" checked={template.is_active !== false} onCheckedChange={(v) => set('is_active', v)} data-testid="tpl-editor-is-active" />
              <Label htmlFor="tpl-active" className="text-sm">Active (turn off to hide without deleting).</Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} className="bg-blue-600 hover:bg-blue-700" data-testid="tpl-editor-save">
            {mode === 'create' ? 'Create Template' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────  InvoiceItemEditor sub-component  ─────────────────
 * Mirrors the actual invoice line-item shape so the SM can author a
 * template once and have every field — supplier, ORP, RRP, margin, qty,
 * labour rate & hours — pre-populated when they pick this template from
 * the QuoteBuilder / InvoiceEdit picker. Shows a live running total so
 * the SM knows what the customer will see before saving.
 */
function InvoiceItemEditor({ template, set }) {
  const c = template.content || {};
  const parts = Number(c.parts_price) || 0;
  const cost = Number(c.parts_cost_price) || 0;
  const margin = Number(c.parts_margin_pct) || 0;
  const qty = Number(c.quantity) || 1;
  const hrs = Number(c.labour_hours) || 0;
  const rate = Number(c.labour_rate) || 0;
  const partsChargedPer = parts * (1 + margin / 100);
  const labourCharged = hrs * rate;
  const lineTotal = (partsChargedPer + labourCharged) * qty;

  return (
    <div className="space-y-3">
      <div>
        <Label>Description</Label>
        <Input value={c.description || ''} onChange={(e) => set('content.description', e.target.value)}
          placeholder="e.g. Standard Service - Sedan" data-testid="tpl-ii-description" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label>Category</Label>
          <Select value={c.category || 'part_and_labour'} onValueChange={(v) => set('content.category', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="part_and_labour">Parts + Labour</SelectItem>
              <SelectItem value="part">Part only</SelectItem>
              <SelectItem value="labour">Labour only</SelectItem>
              <SelectItem value="inspection">Inspection</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 md:col-span-3">
          <Label>Default Supplier (optional)</Label>
          <Input value={c.parts_supplier || ''} onChange={(e) => set('content.parts_supplier', e.target.value)}
            placeholder="Type a supplier name (must match one of your Suppliers templates)" data-testid="tpl-ii-supplier" />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div>
          <Label className="text-rose-700">ORP $ (cost)</Label>
          <Input type="number" min="0" step="0.01" value={c.parts_cost_price ?? 0}
            onChange={(e) => set('content.parts_cost_price', parseFloat(e.target.value) || 0)}
            data-testid="tpl-ii-orp" />
        </div>
        <div>
          <Label>RRP $</Label>
          <Input type="number" min="0" step="0.01" value={c.parts_price ?? 0}
            onChange={(e) => set('content.parts_price', parseFloat(e.target.value) || 0)}
            data-testid="tpl-ii-rrp" />
        </div>
        <div>
          <Label>Margin %</Label>
          <Input type="number" min="0" step="1" value={c.parts_margin_pct ?? 40}
            onChange={(e) => set('content.parts_margin_pct', parseFloat(e.target.value) || 0)}
            data-testid="tpl-ii-margin" />
        </div>
        <div>
          <Label>Quantity</Label>
          <Input type="number" min="0" step="0.5" value={c.quantity ?? 1}
            onChange={(e) => set('content.quantity', parseFloat(e.target.value) || 1)}
            data-testid="tpl-ii-qty" />
        </div>
        <div>
          <Label>Labour Hrs</Label>
          <Input type="number" min="0" step="0.25" value={c.labour_hours ?? 0}
            onChange={(e) => set('content.labour_hours', parseFloat(e.target.value) || 0)}
            data-testid="tpl-ii-hours" />
        </div>
        <div>
          <Label>Lab Rate $/h</Label>
          <Input type="number" min="0" step="0.5" value={c.labour_rate ?? 125.5}
            onChange={(e) => set('content.labour_rate', parseFloat(e.target.value) || 0)}
            data-testid="tpl-ii-rate" />
        </div>
      </div>
      {/* Running total — what the customer will see */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-center justify-between"
           data-testid="tpl-ii-total-row">
        <div className="text-xs text-emerald-700 space-y-0.5">
          <div>Parts: ${parts.toFixed(2)} → ${partsChargedPer.toFixed(2)} <span className="text-emerald-500">(+{margin}% margin)</span></div>
          <div>Labour: {hrs}h × ${rate.toFixed(2)} = ${labourCharged.toFixed(2)}</div>
          {cost > 0 && (
            <div className="text-rose-600">ORP cost: ${(cost * qty).toFixed(2)} · Profit on parts: ${((partsChargedPer - cost) * qty).toFixed(2)}</div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700">Line Total</div>
          <div className="text-xl font-bold text-emerald-900 tabular-nums" data-testid="tpl-ii-total">
            ${lineTotal.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

